<template>
  <section v-if="featuredDeck" class="featured-deck" data-testid="featured-deck">
    <ModalsShare
      :showing="isShowingShareMenu"
      :share-url="shareUrl"
      :title="featuredDeck.title"
      analytics-prefix="CompanyPage"
      @update:showing="isShowingShareMenu = $event"
    />
    <a :href="`/decks/${featuredDeck.url_slug || featuredDeck.id}`">
      <div class="featured text-gray-900">
        <div
          class="featured__img border-gray-200 border"
          data-testid="featured-deck-content-image"
          :class="{ 'bg-gray-900': !featuredDeck.deck_image }"
        >
          <img
            v-if="featuredDeck.deck_image"
            class="absolute h-full object-cover w-full"
            :src="featuredDeck.deck_image"
            :alt="featuredDeck.title"
          />
          <div class="absolute top-2 left-2 lg_top-6 lg_left-6">
            <VTag class="text-white border bg-opacity-30 text-xs lg_text-base bg-gray-900" text="Featured" class-name="" />
          </div>
          <div class="absolute top-2 right-2 lg_hidden">
            <button class="appearance-none p-1 bg-opacity-30 bg-gray-900 rounded-full" @click.prevent="isShowingShareMenu = true">
              <div class="flex justify-evenly items-center text-white">
                <ArrowUpTrayIcon class="w-6" />
              </div>
            </button>
          </div>
          <div class="absolute bottom-2 left-2 lg_hidden">
            <div class="flex items-center">
              <VTag v-for="tag in featuredDeck.type_tags" :key="tag" class="mr-3" :text="tag" />
              <VAssetTag
                v-for="tag in featuredDeck.tags"
                :key="tag"
                class="mr-3"
                :tag="tag"
                :invert-icon-set="true"
                container-classes="bg-opacity-30 text-white"
              />
            </div>
          </div>
        </div>
        <div class="lg_mt-4">
          <div class="hidden mb-4 lg_flex lg_justify-between">
            <div class="flex items-center gap-3">
              <VTag v-for="tag in featuredDeck.type_tags" :key="tag" :text="tag" />
              <VAssetTag v-for="tag in featuredDeck.tags" :key="tag" :tag="tag" />
            </div>
            <button class="post-share justify-self-end appearance-none p-0 bg-transparent" @click.prevent="isShowingShareMenu = true">
              <div class="flex justify-evenly items-center">
                <ArrowUpTrayIcon class="w-6 mr-2" /><span class="font-medium text-sm">Share</span>
              </div>
            </button>
          </div>
          <div class="lg_pt-0 pt-2.5">
            <h3 class="text-lg font-medium mb-2 lg_text-xl lg_font-normal">{{ featuredDeck.title }}</h3>
            <p class="text-gray-700 mb-2 text-sm leading-5 font-medium mb-2 lg_text-base lg_leading-6">{{ featuredDeck.description }}</p>
            <p class="order-1 mb-0 text-xs leading-none text-gray-600 text-sm leading-4 font-medium">
              {{ formatDateFull(featuredDeck.updated_at) }}
            </p>
          </div>
        </div>
      </div>
    </a>
  </section>

  <CompaniesFeaturedDeckEmptyState v-else />
</template>

<script lang="ts" setup>
import { ArrowUpTrayIcon } from '@heroicons/vue/24/outline'
import type { Ref, DeepReadonly } from 'vue'
import type { Deck } from '@/entities/deck'
import type { Company } from '@/entities/company'
import { formatDateFull } from '@/lib/datetime-format-utils'

const props = defineProps({
  company: {
    type: Object as () => Company,
    required: true,
  },
  featuredDeck: {
    type: Object as () => DeepReadonly<Deck> | null,
    default: () => null,
  },
})

const isShowingShareMenu: Ref<boolean> = ref(false)
const shareUrl = computed(() => `${window.location.origin}/decks/${props.featuredDeck?.url_slug || props.featuredDeck?.id}`)
</script>

<style lang="stylus" scoped>
.featured__img
  @apply overflow-hidden relative rounded-lg
  aspect-ratio 16 / 9
</style>
